"use client";

/**
 * Отрисовка состава команды по пяти линиям (KAN-814).
 *
 * Две плотности, потому что состав отвечает на два разных вопроса:
 *  - `RosterLaneStrip` — «насколько команда укомплектована» (плитка каталога);
 *  - `RosterSlotGrid`  — «кого именно не хватает и кто уже есть» (широкая карточка).
 *
 * Обе рисуют одни и те же данные и одни и те же линии в одном порядке, поэтому
 * команда на `/rosters` и на `/lfg` читается одинаково.
 */

import { useTranslations } from "next-intl";

import { cn } from "@/shared/lib/utils";
import { getProfileIconUrl } from "@/shared/lib/champion-image";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/ui/data-display/avatar";
import { Badge } from "@/shared/ui/data-display/badge";
import { RoleIcon, useRoleLabel } from "@/shared/ui/data-display/role-icon";
import { OpggButton } from "@/shared/ui/opgg-button";

import type { RosterLaneSlot, RosterPersonView } from "./types";

/** Максимум подписей в слоте: дальше они перестают читаться. */
const MAX_SLOT_BADGES = 5;

function avatarSrc(member: RosterPersonView | null | undefined): string {
  if (!member) return "";
  if (member.avatarUrl) return member.avatarUrl;
  const iconId = member.riotProfileIconId ?? 0;
  return iconId > 0 ? getProfileIconUrl(iconId) : "";
}

function initial(member: RosterPersonView | null | undefined): string {
  const name = member?.displayName?.trim() ?? "";
  // «?» — это и есть вид свободного слота: имени нет, показывать нечего.
  return name ? name.charAt(0).toUpperCase() : "?";
}

/* ------------------------------------------------------------------ */
/* Компактная полоса линий                                             */
/* ------------------------------------------------------------------ */

export interface RosterLaneStripProps {
  lanes: RosterLaneSlot[];
  className?: string;
}

export function RosterLaneStrip({ lanes, className }: RosterLaneStripProps) {
  const roleLabel = useRoleLabel();
  const tUi = useTranslations("Ui");
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {lanes.map((slot) => {
        const filled = Boolean(slot.member);
        const label = roleLabel(slot.lane);
        return (
          <span
            key={slot.lane}
            title={
              filled && slot.member?.displayName
                ? `${label} — ${slot.member.displayName}`
                : tUi("litRosterLineup.svobodno", { label })
            }
            className={cn(
              "flex size-[26px] shrink-0 items-center justify-center rounded-md border",
              filled
                ? "border-border bg-muted text-foreground"
                : "border-dashed border-border/60 text-muted-foreground/40",
            )}
          >
            <RoleIcon role={slot.lane} size={16} className="text-current" />
          </span>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Развёрнутая сетка слотов                                            */
/* ------------------------------------------------------------------ */

function statusVariant(
  status: RosterLaneSlot["status"],
): "default" | "secondary" | "outline" {
  if (status === "seeking") return "default";
  if (status === "filled") return "secondary";
  return "outline";
}

interface SlotCardProps {
  lane: RosterLaneSlot["lane"] | null;
  slot: Omit<RosterLaneSlot, "lane">;
  onMemberClick?: ((member: RosterPersonView) => void) | undefined;
}

function SlotCard({ lane, slot, onMemberClick }: SlotCardProps) {
  const member = slot.member ?? null;
  const name = member?.displayName?.trim() ?? "";
  const src = avatarSrc(member);
  const roleLabel = useRoleLabel();
  const badges = (slot.badges ?? []).slice(0, MAX_SLOT_BADGES);
  const clickable = Boolean(member && onMemberClick);

  const identity = (
    <>
      <Avatar className="size-12">
        <AvatarImage src={src} alt="" />
        <AvatarFallback className="bg-muted text-sm text-muted-foreground">
          {initial(member)}
        </AvatarFallback>
      </Avatar>
      {name ? (
        <p className="line-clamp-2 w-full px-1 text-center text-xs font-medium text-foreground">
          {name}
        </p>
      ) : null}
    </>
  );

  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card/50 p-2">
      {lane ? (
        <RoleIcon role={lane} size={14} className="opacity-70" />
      ) : null}

      {clickable ? (
        <button
          type="button"
          onClick={() => onMemberClick?.(member!)}
          className="flex w-full flex-col items-center gap-2 rounded-md transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          {identity}
        </button>
      ) : (
        <div className="flex w-full flex-col items-center gap-2">{identity}</div>
      )}

      {badges.length > 0 ? (
        <div className="flex w-full flex-wrap justify-center gap-0.5">
          {badges.map((badge, i) => (
            <Badge
              key={`${badge}-${i}`}
              variant="outline"
              className="h-5 px-1 py-0 text-[9px]"
            >
              {roleLabel(badge)}
            </Badge>
          ))}
        </div>
      ) : null}

      {slot.statusLabel ? (
        <Badge variant={statusVariant(slot.status)} className="text-[10px]">
          {slot.statusLabel}
        </Badge>
      ) : null}

      {member?.opggUrl ? (
        <OpggButton url={member.opggUrl} className="inline-flex h-6 items-center gap-1 rounded-md border border-border px-1.5 text-[10px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground" />
      ) : null}
    </div>
  );
}

export interface RosterSlotGridProps {
  lanes: RosterLaneSlot[];
  /** Запас, тренер, состав без ролей — теми же карточками, но без линии. */
  extras?: RosterPersonView[];
  onMemberClick?: ((member: RosterPersonView) => void) | undefined;
  className?: string;
}

export function RosterSlotGrid({
  lanes,
  extras = [],
  onMemberClick,
  className,
}: RosterSlotGridProps) {
  // Слот несёт смысл, если на нём кто-то есть или если у него есть статус:
  // «Свободно» на доске поиска — это данные, а на карточке команды в профиле
  // пустой слот не сообщает ничего.
  const anyLaneFilled = lanes.some((s) => s.member);
  const lanesInformative = lanes.some((s) => s.member || s.statusLabel);
  // Состав без единой занятой линии, но с людьми — турнирная или временная
  // команда: ролей у неё нет вовсе, и пять «?» вместо состава только мешают.
  const visibleLanes = lanesInformative && (anyLaneFilled || extras.length === 0) ? lanes : [];

  if (visibleLanes.length === 0 && extras.length === 0) return null;

  return (
    <div className={cn("@container/lineup", className)}>
      <div className="grid grid-cols-2 gap-2 @sm/lineup:grid-cols-3 @lg/lineup:grid-cols-5">
        {visibleLanes.map((slot) => (
          <SlotCard
            key={slot.lane}
            lane={slot.lane}
            slot={slot}
            onMemberClick={onMemberClick}
          />
        ))}
        {extras.map((person, i) => (
          <SlotCard
            key={person.id ?? `${person.displayName}-${i}`}
            lane={null}
            slot={{
              member: person,
              badges: person.roleLabel ? [person.roleLabel] : [],
            }}
            onMemberClick={onMemberClick}
          />
        ))}
      </div>
    </div>
  );
}
